// TenantListPage — route: /
// Table of all tenants with Nsofar Sync switch (with AlertDialog), configure button.
// 'Create New Tenant' opens NewTenantWizard.

import { useState } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "../components/AdminNav.js";
import { NewTenantWizard } from "../components/NewTenantWizard.js";
import { listTenants, updateNsofarSync, deleteTenant, type TenantSummary } from "../lib/adminApiClient.js";
import { ApiError } from "../lib/api.js";

const QK = "super-admin-tenants";

const STATUS_DEFAULT = "bg-green-100 text-green-700";
const STATUS_CLS: Record<string, string> = {
  active:    STATUS_DEFAULT,
  trial:     "bg-blue-100 text-blue-700",
  suspended: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-500",
};
const TYPE_DEFAULT = "bg-slate-100 text-slate-600";
const TYPE_CLS: Record<string, string> = {
  standard:        TYPE_DEFAULT,
  nsofar_partner:  "bg-indigo-100 text-indigo-700",
  nsofar_internal: "bg-amber-100 text-amber-700",
};
const COMP_DEFAULT = "bg-slate-100 text-slate-600";
const COMP_CLS: Record<string, string> = {
  standard:          COMP_DEFAULT,
  nsofar_compliance: "bg-purple-100 text-purple-700",
};

function Badge({ label, cls }: { label: string; cls: string }): JSX.Element {
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function NsofarSwitch({ tenant, onToggle }: { tenant: TenantSummary; onToggle: () => void }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const enabled = tenant.nsofar_sync_enabled ?? false;

  async function commit(val: boolean): Promise<void> {
    setBusy(true);
    try {
      await updateNsofarSync(tenant._id, val);
      toast.success(`Nsofar Sync ${val ? "enabled" : "disabled"} for ${tenant.name}.`);
      onToggle();
    } catch {
      toast.error("Failed to update Nsofar Sync.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <button role="switch" aria-checked={enabled} disabled={busy}
        onClick={() => enabled ? setConfirming(true) : void commit(true)}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 ${
          enabled ? "bg-indigo-600" : "bg-slate-300"
        }`}
      >
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0"}`} />
      </button>
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-semibold">Disable Nsofar Sync for {tenant.name}?</h3>
            <p className="text-sm text-slate-500">This will stop DRS submissions and reconciliation for this tenant.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">Cancel</button>
              <button onClick={() => { setConfirming(false); void commit(false); }} className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700">Disable</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Type-the-name confirmation before a hard delete (GitHub-style). Calls the
// guarded DELETE /admin/tenants/:id and reports the deleted-record count.
function DeleteTenantDialog({ tenant, onClose, onDeleted }: {
  tenant: TenantSummary;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = confirmText.trim() === tenant.name;

  async function commit(): Promise<void> {
    setBusy(true);
    try {
      const res = await deleteTenant(tenant._id, tenant.name);
      toast.success(`Deleted "${tenant.name}" — ${res.total} record${res.total === 1 ? "" : "s"} removed.`);
      onDeleted();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      toast.error(body?.error ?? "Delete failed. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h3 className="text-base font-semibold text-red-700">Delete agency “{tenant.name}”?</h3>
        <p className="text-sm text-slate-600">
          This permanently deletes the agency <strong>and all of its data</strong> — users,
          patients, carers, service requests, reports, billing, everything. This cannot be undone.
        </p>
        <div>
          <label htmlFor="confirm-tenant-name" className="block text-xs text-slate-500 mb-1">
            Type <span className="font-mono font-semibold text-slate-700">{tenant.name}</span> to confirm
          </label>
          <input
            id="confirm-tenant-name"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            autoFocus
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => { void commit(); }} disabled={!matches || busy}
            className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? "Deleting…" : "Delete agency"}
          </button>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line complexity -- table render branches over loading/error/empty/has-rows
export function TenantListPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: [QK], queryFn: listTenants });
  const tenants = data?.data ?? [];
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TenantSummary | null>(null);

  function refresh(): void { void qc.invalidateQueries({ queryKey: [QK] }); }

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">Tenants</h1>
          <button onClick={() => setWizardOpen(true)} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700">
            + Create New Tenant
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          {isLoading && <p className="py-10 text-center text-sm text-slate-400">Loading…</p>}
          {isError  && <p className="py-10 text-center text-sm text-red-600">Failed to load tenants.</p>}
          {!isLoading && !isError && tenants.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No tenants found.</p>}
          {!isLoading && !isError && tenants.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                  {["Name","Type","Compliance","Agents","Status","Nsofar Sync","Last Activity",""].map((h) => (
                    <th key={h} className="py-3 px-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {tenants.map((t) => (
                  <tr key={t._id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">
                      <Link to={`/tenants/${t._id}`} className="hover:text-indigo-600">{t.name}</Link>
                    </td>
                    <td className="py-3 px-4"><Badge label={t.tenant_type ?? "standard"} cls={TYPE_CLS[t.tenant_type ?? ""] ?? TYPE_DEFAULT} /></td>
                    <td className="py-3 px-4"><Badge label={t.compliance_mode ?? "standard"} cls={COMP_CLS[t.compliance_mode ?? ""] ?? COMP_DEFAULT} /></td>
                    <td className="py-3 px-4 text-slate-500">{t.active_agents ?? "—"}</td>
                    <td className="py-3 px-4"><Badge label={t.status} cls={STATUS_CLS[t.status] ?? STATUS_DEFAULT} /></td>
                    <td className="py-3 px-4"><NsofarSwitch tenant={t} onToggle={refresh} /></td>
                    <td className="py-3 px-4 text-slate-400 text-xs">
                      {t.last_activity ? formatDistanceToNow(new Date(t.last_activity), { addSuffix: true }) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Link to={`/tenants/${t._id}`} className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                          <Settings className="h-3.5 w-3.5" /> Configure
                        </Link>
                        <button onClick={() => setDeleteTarget(t)} title={`Delete ${t.name}`} aria-label={`Delete ${t.name}`}
                          className="flex items-center px-2 py-1 text-xs font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {wizardOpen && <NewTenantWizard onClose={() => setWizardOpen(false)} onCreated={() => { setWizardOpen(false); refresh(); }} />}
      {deleteTarget && (
        <DeleteTenantDialog
          tenant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); refresh(); }}
        />
      )}
    </div>
  );
}
